<template>
    <div class="py-6 px-4 sm:p-6 lg:pb-8">
        <div>
            <h2 class="text-lg leading-6 font-medium text-gray-900">Coverage Map</h2>
            <p class="mt-1 text-sm text-gray-500">
                Specify the locations you are prepared to teach in.
            </p>
        </div>

        <div class="mt-6 grid grid-cols-12">
            <div class="col-span-12 max-w-sm">
                <label for="residence" class="block text-sm font-medium text-gray-700">
                    Where do you currently reside?
                </label>
                <p class="text-xs text-gray-500">
                    This will be used to tailor your experience with The Tutor Index and improve the clients you are matched with.
                    You <span class="font-semibold">do not</span> necessarily need to tutor at/from this location.
                </p>
                <VueGoogleAutocomplete 
                    v-if="googleHasLoaded"
                    v-bind:value="residence"
                    ref="residenceInput"
                    id="residence"
                    placeholder="e.g. Windsor, UK"
                    types="(cities)"
                    :fields="['address_components', 'vicinity', 'geometry']"
                    @placechanged="onPlaceChanged"
                    class="mt-2 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
            </div>
        </div>

        <div class="mt-2 py-4 flex items-center justify-between">
            <div class="flex flex-col">
                <p @click="toggleTeachOnline()" class="text-sm font-medium text-gray-900" id="privacy-option-1-label">
                Do you teach online?
                </p>
                <!-- <p class="text-sm text-gray-500" id="privacy-option-1-description">
                If you 
                </p> -->
            </div>

            <tti-toggle :value="$store.state.user.TeachesOnline" @onChange="value => toggleTeachOnline(value)"></tti-toggle>
        </div>

        <div class="mt-3 flex items-center space-x-3">
            <h2 class="text-lg leading-6 font-medium text-gray-900">Your Locations</h2>
            <tti-button v-if="locations.length > 0 && !isAddingNewLocation" @click="isAddingNewLocation = true" size="xsmall">Add Another Location</tti-button>
        </div>
        
        <tti-button v-if="locations.length === 0 && !isAddingNewLocation" @click="isAddingNewLocation = true" class="mt-3">New Location</tti-button>


        
        <!-- This example requires Tailwind CSS v2.0+ -->
        <div v-if="isAddingNewLocation" class="bg-gray-50 overflow-hidden rounded-lg mt-3">
            <div class="px-4 py-5 sm:p-6">
            <!-- Content goes here -->

                <form @submit.prevent="onAddLocation">
                    <div class="grid grid-cols-12 divide-y divide-gray-200">
                        <div class="col-span-12 sm:col-span-6">
                            <label for="postcode" class="block text-sm font-medium text-gray-700">Postode</label>
                            <input v-model.trim="newLocationPostcode" type="text" name="postcode" id="postcode" autocomplete="postal-code" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm uppercase placeholder-gray-400 placeholder-normal-case" placeholder="e.g. DN55 1PT">
                            <p class="mt-2 text-sm text-gray-500 font-semibold">Postcodes will <span class="italic">not</span> be publicly visible.</p>
                            <p v-if="$v.newLocationPostcode.$dirty && !$v.newLocationPostcode.required" class="mt-2 text-sm text-red-600">Please enter a postcode.</p>
                            <p v-if="!$v.newLocationPostcode.recognised" class="mt-2 text-sm text-red-600">Sorry, we don’t recognise that postcode.</p>
                        </div>
                    </div>


                    <div class="mt-6 flex items-start justify-between">
                        <div @click="newLocationTeachAtLocation = !newLocationTeachAtLocation" class="flex flex-col">
                            <p class="text-sm font-medium text-gray-900" id="privacy-option-1-label">
                                Can you teach at this location?
                            </p>
                            <p class="text-sm text-gray-500" id="privacy-option-1-description">
                                Enable this option if the location represents physical premises you plan to teach in, such as your home or a specific local library or coffee shop. Students will travel to meet with you at this location.
                            </p>
                        </div>

                        <tti-toggle :value="newLocationTeachAtLocation" @onChange="(value) => newLocationTeachAtLocation = value"></tti-toggle>
                    </div>

                    <div class="mt-6 flex items-start justify-between">
                        <div @click="newLocationPreparedToTravel = !newLocationPreparedToTravel" class="flex flex-col">
                            <p class="text-sm font-medium text-gray-900" id="privacy-option-1-label">
                                Can you travel from this location to teach elsewhere?
                            </p>
                            <p class="text-sm text-gray-500" id="privacy-option-1-description">
                                Enable this option if you plan to travel from this location in order to teach at a location specified by the student, for example, their home or school.
                            </p>
                        </div>

                        <tti-toggle :value="newLocationPreparedToTravel" @onChange="(value) => newLocationPreparedToTravel = value"></tti-toggle>
                    </div>

                    <div v-if="newLocationPreparedToTravel" class="mt-2 grid grid-cols-12 gap-6 divide-y divide-gray-200">
                        <div class="col-span-12 sm:col-span-6 space-y-1">
                            <label for="website" class="block text-sm font-medium text-gray-700">How far are you prepared to travel?</label>
                            <div class="flex items-center space-x-2">
                                <input v-model.number="newLocationRadiusStep" type="range" min="0" max="8" step="1" />
                                <span>{{ newLocationRadius }} mile{{ newLocationRadius === 1 ? '' : 's' }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 space-x-2">
                        <tti-button type="submit">Add Location</tti-button>
                        <tti-button @click="onCancelAddLocation" theme="outline">Cancel</tti-button>
                    </div>

                    <p v-if="($v.newLocationTeachAtLocation.$dirty || $v.newLocationPreparedToTravel.$dirty) && (!$v.newLocationTeachAtLocation.checked && !$v.newLocationPreparedToTravel.checked)" class="mt-3 text-sm text-red-600">
                        Please specify if you will teach at this location and/or are prepared to travel.
                    </p>

                </form>

            </div>
        </div>


        <img v-if="locations.length > 0" :src="mapSrc" class="mt-3 rounded max-h-96" />

        <div>
            <div v-for="(location, index) in locations" :key="`location${index}`" class="mt-4">
                <div class="flex items-start space-x-2">
                    <button type="button" @click="onDeleteLocation(location, index)" class="mt-0.5 flex items-center space-x-1 text-gray-500 hover:text-gray-700">
                        <heroicon icon="trash" class="w-5 h-5"></heroicon>
                        <!-- <span class="text-sm hidden sm:block">Remove</span> -->
                    </button>
                    <div>
                        <div class="flex justify-between items-center max-w-xl">
                            <div class="font-medium text-gray-800">{{ location.Parish }}, {{ location.Postcode }}</div>
                        </div>
                        <div class="mt-1 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-6">
                            <div v-if="location.WillTeachAtLocation" class="flex flex-grow-0 items-center space-x-1">
                                <div class="w-7 flex justify-center">
                                    <heroicon icon="location-marker" type="solid" class="w-7 h-7 text-blue-600"></heroicon>
                                </div>
                                <span>You can teach at this location</span>
                            </div>
                            <!-- <div v-else class="flex flex-grow-0 items-center space-x-1"><heroicon icon="x-circle" class="w-6 h-6 text-gray-500"></heroicon> <span>Does not teach at this location</span></div> -->

                            <div v-if="location.WillTravelFromLocation" class="flex flex-grow-0 items-center space-x-1">
                                <div class="w-6 h-6 mx-0.5 rounded-full bg-blue-600 bg-opacity-40"></div>
                                <span>You can travel {{ location.Radius }} mile{{ location.Radius === 1 ? '' : 's' }} from this location</span>
                            </div>
                            <!-- <div v-else class="flex flex-grow-0 items-center space-x-1"><heroicon icon="x-circle" class="w-6 h-6 text-gray-500"></heroicon> <span>Does not travel from this location</span></div> -->
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
       
    </div>
</template>
<script>
import Vue from 'vue'
import axios from 'axios';
import { required, requiredUnless } from 'vuelidate/lib/validators'

import VueGoogleAutocomplete from 'vue-google-autocomplete';

import TutorService from '../services/TutorServices'
const tutorService = new TutorService();

import CountriesService from '../services/CountriesService'
const countriesService = new CountriesService()

export default {
    components: {
        'tti-toggle': () => import('./TailwindToggle'),
        'tti-button': () => import('./TailwindButton'),
        'heroicon': () => import('./Heroicon'),
        VueGoogleAutocomplete,
    },
    data() {
        return {
            isTutoringOnline: false,
            isAddingNewLocation: false,
            newLocationPostcode: null,
            newLocationRadiusStep: 3,
            newLocationTeachAtLocation: false,
            newLocationPreparedToTravel: false,
            newLocationValidationError: null,
            unrecognisedPostcode: false,
            locations: [],
            countries: [],
            residence: null,
            googleHasLoaded: false,
        }
    },
    validations: {
        newLocationPostcode: {
            required,
            recognised: function() { return !this.unrecognisedPostcode }
        },
        newLocationPreparedToTravel: {
            checked: function(value) { return value === true || this.newLocationTeachAtLocation === true },
        },
        newLocationTeachAtLocation: {
            checked: function(value) { return value === true || this.newLocationPreparedToTravel === true }
        }
    },
    watch: {
        newLocationPostcode: function() {
            this.unrecognisedPostcode = false;
        },
    },
    computed: {
        // Google Maps
        // mapSrc() {
        //     let url = 'https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyAAsIATBbPpaX4NlWUJwaY0WZtXIt9dt20&size=400x400';

        //     for (let i = 0; i < this.locations.length; i++) {
        //         const element = this.locations[i];
        //         url = `${url}&markers=${element.latitude},${element.longitude}`
        //     }

        //     return url;
        // }

        // Map Quest
        mapSrc() {
            let url = 'https://www.mapquestapi.com/staticmap/v5/map?key=5tVwA2AXb9IvhdwYONqpebwzXwYKw24L&size=500,350@2x&defaultMarker=marker-md-1e3a8a';
            let markers = [];
            let areas = [];

            for (const location of this.locations) {
                if (location.WillTeachAtLocation)
                    markers.push(`${location.Latitude},${location.Longitude}`);
                
                if (location.WillTravelFromLocation)
                    areas.push(`&shape=border:00000000|fill:2563EB88|radius:${location.Radius}mi|${location.Latitude},${location.Longitude}`);
            }

            if (markers.length > 0)
                url = `${url}&locations=${markers.join('||')}`;

            if (areas.length > 0)
                url = `${url}${areas.join('')}`;

            return url;
        },
        newLocationRadius() {
            return [1, 2, 3, 5, 10, 15, 20, 30, 50][this.newLocationRadiusStep];
        }
    },
    methods: {
        async onAddLocation() {
            this.$v.$touch();

            if (this.$v.$invalid)
                return;

            var locationDetails;
            this.unrecognisedPostcode = false;

            try {
                locationDetails = await this.getDetailsForPostcode(this.newLocationPostcode);    
            } catch {
                try {
                    locationDetails = await this.getDetailsForOutcode(this.newLocationPostcode);    
                } catch {
                    this.unrecognisedPostcode = true;
                    return;
                }
            }
            

            Object.assign(locationDetails, 
                { WillTravelFromLocation: this.newLocationPreparedToTravel, Radius: this.newLocationRadius },
                { WillTeachAtLocation: this.newLocationTeachAtLocation });

            var response = await tutorService.addLocation(locationDetails);

            locationDetails.Id = response.data
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Location added.' });

            
            this.locations.push(locationDetails);

            this.resetAddLocationForm();
            this.isAddingNewLocation = false;
        },
        onCancelAddLocation() {
            this.resetAddLocationForm();
            this.isAddingNewLocation = false;
        },
        async getDetailsForPostcode(postcode) {
            let response = await axios.get(`https://api.postcodes.io/postcodes/${postcode}`);
            var postcodeDetails = response.data.result;

            return { 
                Postcode: postcodeDetails.postcode,
                Parish: postcodeDetails.parish.replace(', unparished area', ''),
                Latitude: postcodeDetails.latitude,
                Longitude: postcodeDetails.longitude
            };
        },
        async getDetailsForOutcode(outcode) {
            let response = await axios.get(`https://api.postcodes.io/outcodes/${outcode}`);
            var outcodeDetails = response.data.result;

            return {
                Postcode: outcodeDetails.outcode,
                Parish: outcodeDetails.parish[0].replace(', unparished area', ''),
                Latitude: outcodeDetails.latitude,
                Longitude: outcodeDetails.longitude
            }
        },
        async onDeleteLocation(location, index) {
            await tutorService.deleteLocation(location.Id);
            this.$delete(this.locations, index);
        },
        resetAddLocationForm() {
            this.newLocationPreparedToTravel = false;
            this.newLocationTeachAtLocation = false;
            this.newLocationRadiusStep = 3;
            this.newLocationPostcode = null;
            this.$nextTick(() => { this.$v.$reset(); });
        },
        async toggleTeachOnline(newVal) {
          await this.$store.dispatch('updateUser', { TeachesOnline: newVal || !this.$store.state.user.TeachesOnline });
          this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
        },
        async getCountries() {
            var countries = await countriesService.get()
            this.countries = countries.data
        },
        async onPlaceChanged(_, result) {
            let area = ''

            var line1 = result.address_components[0].long_name
            var postalTown = result.address_components.find(x => x.types.some(y => y === 'postal_town'))?.long_name

            if (postalTown) {
                area = line1 === postalTown 
                    ? `${line1}`
                    : `${line1}, ${postalTown}`
            }
            else {
                area = line1
            }

            if (!area) 
                area = result.vicinity
                    
            const country = result.address_components.find(x => x.types.some(y => y === 'country'))?.long_name

            await this.$store.dispatch('updateUser', { ResidenceTown: area, ResidenceCountry: country });
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
        },
        setVueAutocompleteOptions() {
            // We can't set these options on the component, so we do it
            // when it has loaded instead.

            // This sets the bounds to the entirety of the UK. Using bounds
            // allows to us to have strictBounds set to false (the default)
            // which means there will be a bias toward the UK, but any location
            // in the world can still be picked. If we used 'componentRestrictions'
            // instead and set a country then it wouldn't be possible to select 
            // rest of the world.
            this.$refs.residenceInput.autocomplete.setBounds({ east: 2, west: -9, north: 61, south: 49 })
        }
    },
    mounted() {
        this.resetAddLocationForm();
        this.getCountries()

        this.locations = this.$store.state.user.Locations;

        var resides = []
        if (this.$store.state.user.ResidenceTown) resides.push(this.$store.state.user.ResidenceTown)
        if (this.$store.state.user.ResidenceCountry) resides.push(this.$store.state.user.ResidenceCountry)
        this.residence = resides.join(', ')
    },
    created() {
        this.$loadScript("https://maps.googleapis.com/maps/api/js?key=AIzaSyAAsIATBbPpaX4NlWUJwaY0WZtXIt9dt20&libraries=places")
            .then(() => {
                console.log("Google Maps loaded.")
                this.googleHasLoaded = true

                Vue.nextTick(() => this.setVueAutocompleteOptions())
            })
    }
}
</script>